import { ref, watch, onMounted, onUnmounted, type ComputedRef } from 'vue'

const END_OF_SHIFT_POLL_INTERVAL_MS = 10 * 1000
const THROTTLE_MS = 5 * 1000

export function useDynamicPolling(
  onShiftCallback: () => void | Promise<void>,
  intervalMs: number,
  hasNoShift?: ComputedRef<boolean>,
  noShiftCallback?: () => void | Promise<void>
) {
  const isPollingActive = ref(false)
  let pollIntervalId: number | null = null
  let timeCheckId: number | null = null
  let lastPollTime = 0

  function shouldPoll() {
    if (hasNoShift?.value) {
      return !!noShiftCallback
    } else {
      return true
    }
  }

  async function performPollStep() {
    if (!shouldPoll()) {
      stopPolling()
      return
    }

    lastPollTime = Date.now()

    try {
      if (hasNoShift?.value && noShiftCallback) {
        await noShiftCallback()
      } else {
        await onShiftCallback()
      }
    } catch (error) {
      console.error('[useDynamicPolling] Callback error:', error)
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible' && isPollingActive.value) {
      const now = Date.now()
      if (now - lastPollTime > THROTTLE_MS) {
        stopPolling()
        startPolling()
      }
    }
  }

  function startPolling() {
    if (isPollingActive.value) return
    isPollingActive.value = true

    if (shouldPoll()) {
      performPollStep()
    }

    pollIntervalId = window.setInterval(performPollStep, intervalMs)
  }

  function stopPolling() {
    if (!isPollingActive.value) return
    isPollingActive.value = false
    if (pollIntervalId !== null) {
      clearInterval(pollIntervalId)
      pollIntervalId = null
    }
  }

  function timeChecker() {
    if (!shouldPoll()) {
      stopPolling()
    } else if (!isPollingActive.value) {
      startPolling()
    }
  }

  if (hasNoShift) {
    watch(
      hasNoShift,
      () => {
        timeChecker()
      },
      { immediate: true }
    )
  }

  onMounted(() => {
    timeChecker()
    timeCheckId = window.setInterval(timeChecker, END_OF_SHIFT_POLL_INTERVAL_MS)

    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    stopPolling()
    if (timeCheckId !== null) {
      clearInterval(timeCheckId)
      timeCheckId = null
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })
}
